import { createTheme } from '@mui/material/styles';

// Single accent for the whole admin surface (indigo) — distinct from the
// success/error/warning semantic colors used in status chips and alerts.
export const adminColors = {
  accent: '#4f46e5',
  accentDark: '#4338ca',
  accentLight: '#eef2ff',
  accentBorder: '#c7d2fe',
  surface: '#f7f7f8',
  surfaceRaised: '#ffffff',
  ink: '#18181b',
  inkSoft: '#52525b',
  sidebar: '#16181d',
  sidebarBorder: 'rgba(255,255,255,0.08)',
  // AI/assistant message avatar — a fixed, deliberately non-brand green
  // (never the indigo accent, so a "the assistant said this" avatar can't
  // be mistaken for a user's) shared by SafetyFlagsView's ConversationDialog
  // and SafetyReviewPage's context view, the two places that render a
  // message-by-message transcript. Named here so it's one token instead of
  // the same bare '#43a047' literal duplicated in both files.
  aiAvatar: '#43a047',
  // MUI's default warning.main (#ed6c02) measures 3.11:1 against white —
  // below the 4.5:1 AA floor for normal text — yet it's used as a filled
  // Alert background with white text, the severity teachers see for every
  // validation message. This is a darker step down the same orange hue that
  // clears 4.5:1 with real margin (5.36:1), matching the buffer this
  // codebase already treats as its own safety floor elsewhere (see
  // tutorTheme.js's inkFaint comment on why sub-5:1 margins are risky).
  // Scoped to the MuiAlert filled+warning override below only — it doesn't
  // replace palette.warning.main globally, so outlined/standard warning
  // Alerts, warning Chips, etc. are unaffected and would need their own
  // audit before reusing this value.
  warningAlertBg: '#b8460a',
  // Semantic success/error pair for chat result cards (code execution,
  // document generation, the assistant's own inline error bubble) — these
  // used to be three independent sets of raw Material/Tailwind hex literals
  // (#4caf50/#e8f5e8, #f44336/#ffebee, #059669) that didn't match each
  // other or anything else in the app. `success`/`successLight` are emerald
  // (a deliberately different hue from `aiAvatar`'s green so "this action
  // succeeded" can't be misread as "the assistant said this"); `error`/
  // `errorLight`/`errorBorder` are the same red family as palette.error
  // above, just exposed as plain hex here for files that read adminColors
  // directly instead of going through the MUI theme. `success` (#047857)
  // is the emerald-700 step, not emerald-600 (#059669, the value this
  // replaces) — #059669 only measures 3.77:1 on white, below the 4.5:1 AA
  // floor for normal text; #047857 clears it at 5.48:1.
  success: '#047857',
  successLight: '#ecfdf5',
  successBorder: '#a7f3d0',
  error: '#dc2626',
  errorDark: '#991b1b',
  errorLight: '#fef2f2',
  errorBorder: '#fca5a5',
};

// Visual icon size can stay small; this pads the hit area out to the
// 44x44 touch-target minimum on touch input without changing desktop
// density. Shared by every icon-button-style control in the chat/sidebar
// UI so the touch-target rule lives in one place instead of five.
// Apply this to the actual interactive element (a Button/IconButton root,
// or `& .MuiOutlinedInput-root` / `& .MuiSelect-select` for text fields and
// selects) — not to a FormControl/TextField's outer root, which is a
// non-interactive layout wrapper and will just inflate empty space around
// the control instead of enlarging its hit area.
export const TOUCH_HIT_SX = {
  "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 }
};

// adminColors.accent (#4f46e5) only computes ~2.8:1 contrast against the
// dark sidebar background (adminColors.sidebar), below the 3:1 WCAG 1.4.11
// minimum for non-text UI indicators. Use this lighter indigo for
// focus-visible rings drawn on dark sidebar/rail surfaces instead — shared
// by ChatSidebar, ChatSidebarItem, and AdminDashboardV2's nav so every
// dark-chrome focus ring in the app is the same color, not three
// independently-tuned literals.
export const FOCUS_RING_ON_DARK = "#a5b4fc";

// Named corner-radius scale for the admin/games surfaces. `sx` borderRadius
// is a MULTIPLIER of theme.shape.borderRadius (10 here), so unitless values
// like `borderRadius: 2` or `borderRadius: 3` silently render at 20px/30px
// and drift out of sync between files. Use these explicit pixel constants
// instead of unitless numbers on any surface/control built with `sx`.
export const radius = {
  control: '8px',  // buttons, inputs, selects, small nested chips
  surface: '12px', // cards, panels, table containers, toolbars
  pill: '999px',   // fully-rounded badges/pills
};

// Mirrors the MuiAvatar styleOverrides.root.borderRadius below (10, i.e.
// 10px — that override is a plain style object, not run through `sx`'s
// unitless-number-as-multiplier transform, so it's already an absolute
// pixel value despite the bare number). A real MUI <Avatar> always picks
// this up automatically via the theme; this export exists only for the
// hand-built collapsed-rail avatar controls in ChatSidebar.js and
// AdminDashboardV2.js, which aren't <Avatar> components and would
// otherwise need to hardcode their own guess at the shape (previously a
// bare '50%' circle in ChatSidebar's rail, silently drifting from the
// rounded-square every real <Avatar> uses everywhere else — including the
// same rail's own account menu, one click away).
export const AVATAR_RADIUS = '10px';

// Base tokens shared across the app (grey ramp, background, text, base
// typography/shape/component defaults). This used to live in a separate
// theme file and get merged in via createTheme(otherTheme, {...}) — now
// it's inlined here so adminTheme is the single, self-contained global
// theme.
const baseThemeOptions = {
  palette: {
    // Overridden below by the adminOverrides createTheme argument; kept here
    // only so this base object is a complete, standalone palette shape.
    primary: {
      main: adminColors.accent,
      light: adminColors.accentBorder,
      dark: adminColors.accentDark,
    },
    secondary: {
      main: '#6366f1',
      light: '#8b5cf6',
      dark: '#4338ca',
    },
    // Canonical danger red — every MUI `color="error"` consumer (Buttons,
    // Alerts, Chips, the Snackbar's error severity, plain
    // `theme.palette.error.*` reads) resolves through this one token, so it
    // always matches the severity scale used on the safety surfaces
    // (SafetyFlagsView's `high` severity dot/badge is this same #dc2626).
    // `dark`/`light` are real Tailwind red-800/red-400 companions (not MUI's
    // mismatched red-800/red-300-ish defaults for a d32f2f main), chosen to
    // read correctly against both light surfaces (dark, for text/hover) and
    // the dark sidebar chrome (light, e.g. the Sign Out button).
    error: {
      main: '#dc2626',
      dark: '#991b1b',
      light: '#f87171',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f7f7f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#374151',
      secondary: '#6b7280',
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontSize: '16px',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '14px',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
};

export const adminTheme = createTheme(baseThemeOptions, {
  palette: {
    primary: {
      main: adminColors.accent,
      dark: adminColors.accentDark,
      light: adminColors.accentBorder,
    },
    background: {
      default: adminColors.surface,
      paper: adminColors.surfaceRaised,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 1px 2px rgba(24,24,27,0.04), 0 1px 0 rgba(24,24,27,0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(24,24,27,0.04), 0 1px 0 rgba(24,24,27,0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 10px rgba(79,70,229,0.25)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: '0.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: adminColors.inkSoft,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.12s ease',
          '&:hover': {
            // 4% read as effectively invisible on the clickable
            // Students/Classes rows — raised to 8% for a hover state
            // that's actually perceptible without overpowering selected-row
            // shading.
            backgroundColor: 'rgba(79,70,229,0.08)',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    // Filled `warning` Alerts (validation messages) fail WCAG AA with MUI's
    // default warning.main + white text (3.11:1). Only the filled+warning
    // combination is touched here — see adminColors.warningAlertBg above for
    // the contrast rationale and why this isn't a palette.warning.main change.
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === 'filled' &&
            ownerState.color === 'warning' && {
              backgroundColor: adminColors.warningAlertBg,
              color: '#ffffff',
              '& .MuiAlert-icon': {
                color: '#ffffff',
              },
            }),
        }),
      },
    },
  },
});

export default adminTheme;
